import { useEffect, useMemo, useState } from "react";
import api, { fileUrl, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Store, Search, Plus, MapPin, ImageIcon } from "lucide-react";
import MessageButton from "@/components/MessageButton";

const CATEGORIES = [
  { id: "all", label: "All" },
  { id: "furniture", label: "Furniture" },
  { id: "kids", label: "Kids" },
  { id: "food", label: "Food" },
  { id: "electronics", label: "Electronics" },
  { id: "fashion", label: "Fashion" },
  { id: "sports", label: "Sports" },
];

function ksh(n) {
  try { return "KES " + Number(n).toLocaleString("en-KE"); }
  catch { return `KES ${n}`; }
}

export default function Marketplace() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cat, setCat] = useState("all");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [imagePath, setImagePath] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({
    title: "", category: "furniture", description: "", price_kes: "", condition: "used", location: "",
  });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/marketplace");
      setItems(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { document.title = "Marketplace · Jirani"; load(); }, []);

  const filtered = useMemo(() => {
    return items
      .filter((i) => cat === "all" || i.category === cat)
      .filter((i) =>
        !q.trim() ||
        i.title.toLowerCase().includes(q.toLowerCase()) ||
        (i.description || "").toLowerCase().includes(q.toLowerCase())
      );
  }, [items, cat, q]);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImagePath(data.path);
      toast.success("Photo added");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, price_kes: parseFloat(form.price_kes) || 0, image_path: imagePath };
      const { data } = await api.post("/marketplace", payload);
      setItems([data, ...items]);
      setOpen(false);
      setImagePath(null);
      setForm({ title: "", category: "furniture", description: "", price_kes: "", condition: "used", location: "" });
      toast.success("Listing posted! Karibu buyers.");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="marketplace-page" className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
      <header className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="jirani-eyebrow">Buy & sell nearby</div>
          <h1 className="mt-2 flex items-center gap-3 text-4xl tracking-tight text-[#1A1A1A]" style={{ fontFamily: "Outfit", fontWeight: 800 }}>
            <Store className="h-8 w-8 text-[#D95D39]" /> Marketplace
          </h1>
          <p className="mt-2 max-w-xl text-[#4A4A4A]">
            Fresh finds from your neighbors — from mahogany tables to home-baked sourdough.
          </p>
        </div>

        {user && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button
                data-testid="new-listing-btn"
                className="jirani-pill rounded-full bg-[#D95D39] px-5 text-white hover:bg-[#c25333]"
              >
                <Plus className="mr-2 h-4 w-4" /> Sell something
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: "Outfit" }}>Create a listing</DialogTitle>
              </DialogHeader>
              <form onSubmit={submit} className="space-y-3">
                <div>
                  <Label>Title</Label>
                  <Input data-testid="listing-title-input" required maxLength={140}
                    value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                    className="mt-1 h-11 rounded-xl" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Price (KES)</Label>
                    <Input data-testid="listing-price-input" type="number" min="0" required
                      value={form.price_kes} onChange={(e) => setForm({ ...form, price_kes: e.target.value })}
                      className="mt-1 h-11 rounded-xl" />
                  </div>
                  <div>
                    <Label>Category</Label>
                    <select data-testid="listing-category-select"
                      value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                      className="mt-1 h-11 w-full rounded-xl border border-black/10 bg-white px-3">
                      {CATEGORIES.filter((c) => c.id !== "all").map((c) => (
                        <option key={c.id} value={c.id}>{c.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Condition</Label>
                    <select data-testid="listing-condition-select"
                      value={form.condition} onChange={(e) => setForm({ ...form, condition: e.target.value })}
                      className="mt-1 h-11 w-full rounded-xl border border-black/10 bg-white px-3">
                      <option value="new">New</option>
                      <option value="like_new">Like new</option>
                      <option value="used">Used</option>
                    </select>
                  </div>
                  <div>
                    <Label>Location</Label>
                    <Input data-testid="listing-location-input" required
                      value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })}
                      className="mt-1 h-11 rounded-xl" placeholder="Karen" />
                  </div>
                </div>
                <div>
                  <Label>Description</Label>
                  <Textarea data-testid="listing-description-input" rows={4}
                    value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                    className="mt-1 rounded-xl" />
                </div>
                <div>
                  <label
                    data-testid="listing-image-label"
                    className="jirani-pill inline-flex cursor-pointer items-center gap-1 rounded-full border border-black/10 bg-white px-3 py-1.5 text-xs text-[#2E473B] hover:bg-[#F9F7F4]"
                  >
                    <ImageIcon className="h-3.5 w-3.5" />
                    {uploading ? "Uploading..." : imagePath ? "Change photo" : "Add photo"}
                    <input
                      data-testid="listing-image-input"
                      type="file" accept="image/*" className="hidden"
                      onChange={(e) => upload(e.target.files?.[0])}
                    />
                  </label>
                  {imagePath && (
                    <img src={fileUrl(imagePath)} alt="preview" className="mt-3 max-h-40 rounded-xl" />
                  )}
                </div>
                <DialogFooter>
                  <Button data-testid="listing-submit-btn" type="submit" disabled={saving}
                    className="jirani-pill w-full rounded-full bg-[#D95D39] text-white hover:bg-[#c25333]">
                    {saving ? "Posting..." : "Publish listing"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </header>

      <div className="mb-6 flex flex-wrap items-center gap-3">
        <div className="relative w-full max-w-md">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#2E473B]/60" />
          <Input data-testid="marketplace-search"
            value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search listings"
            className="h-11 rounded-full border-black/10 bg-white pl-9" />
        </div>
        <div className="flex flex-wrap gap-2">
          {CATEGORIES.map((c) => (
            <button key={c.id}
              data-testid={`mp-cat-${c.id}`}
              onClick={() => setCat(c.id)}
              className={`jirani-pill rounded-full px-4 py-1.5 text-xs font-medium ${
                cat === c.id
                  ? "bg-[#2E473B] text-white"
                  : "border border-black/10 bg-white text-[#2E473B] hover:bg-[#F9F7F4]"
              }`}>
              {c.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="grid gap-4 grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
          {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
            <div key={i} className="h-64 animate-pulse rounded-2xl border border-black/5 bg-white/60" />
          ))}
        </div>
      ) : (
        <div data-testid="marketplace-list" className="grid gap-4 grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
          {filtered.map((l) => (
            <article
              key={l.id}
              data-testid={`listing-${l.id}`}
              className="jirani-card group overflow-hidden rounded-2xl border border-black/5 bg-white"
            >
              <div className="aspect-square w-full overflow-hidden bg-[#F9F7F4]">
                {l.image_path ? (
                  <img src={fileUrl(l.image_path)} alt={l.title}
                    className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" />
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-[#2E473B]/40">
                    <ImageIcon className="h-10 w-10" />
                  </div>
                )}
              </div>
              <div className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="line-clamp-1 text-sm font-semibold text-[#1A1A1A]" style={{ fontFamily: "Outfit" }}>
                    {l.title}
                  </h3>
                  <Badge className="shrink-0 rounded-full bg-[#F9F7F4] text-[10px] text-[#2E473B] hover:bg-[#F9F7F4]">
                    {l.condition.replace("_", " ")}
                  </Badge>
                </div>
                <div className="mt-2 text-base font-bold text-[#D95D39]" style={{ fontFamily: "Outfit" }}>
                  {ksh(l.price_kes)}
                </div>
                <p className="mt-1 line-clamp-2 text-xs text-[#4A4A4A]">{l.description}</p>
                <div className="mt-3 flex items-center justify-between text-[11px] text-[#2E473B]/80">
                  <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {l.location}</span>
                  <span>{l.author?.name?.split(" ")[0] || "Neighbor"}</span>
                </div>
                {l.author?.user_id && (
                  <div className="mt-3">
                    <MessageButton peerId={l.author.user_id} peerName={l.author.name} className="w-full justify-center" />
                  </div>
                )}
              </div>
            </article>
          ))}
          {filtered.length === 0 && (
            <div className="col-span-full rounded-2xl border border-dashed border-black/10 bg-white p-10 text-center text-[#4A4A4A]">
              Nothing here yet — be the first to list!
            </div>
          )}
        </div>
      )}
    </div>
  );
}
